/**
 * Process Execution API Client
 * Handles the parent ProcessExecution entity — the actually-maintained source of truth for
 * completion status/timing/node-counts. ProcessThreadExecution (see
 * processThreadExecutionApiClient.ts) has its own copies of these same fields, but they're
 * written once at creation and never updated on completion; only this entity is kept current
 * by ProcessExecutionStatusUpdaterService.UpdateExecutionCompletedAsync/UpdateExecutionFailedAsync.
 */

import { BaseApiClient, type ApiResponse, RequestOptions } from './baseApiClient';
import { API_ENDPOINTS } from '../config/apiEndpoints';
import { normalizeServerDate } from '../utils/serverDate';

interface ProcessExecutionDto {
  ProcessExecutionID: number;
  ProcessID: number;
  ExecutionModeID: number;
  ExecutionStatusID: number;
  StartedAt: string;
  StoppedAt?: string | null;
  Duration?: number | null;
  ErrorMessage?: string | null;
  TotalNodes?: number | null;
  CompletedNodes?: number | null;
  FailedNodes?: number | null;
  SkippedNodes?: number | null;
}

export interface ProcessExecutionCompletionInfo {
  processExecutionId: number;
  processId: number;
  executionModeId: number;
  executionStatusId: number;
  startedAt: string;
  stoppedAt?: string;
  duration?: number;
  totalNodes?: number;
  completedNodes?: number;
  failedNodes?: number;
  skippedNodes?: number;
  errorMessage?: string;
}

function mapDto(dto: ProcessExecutionDto): ProcessExecutionCompletionInfo {
  return {
    processExecutionId: dto.ProcessExecutionID,
    processId: dto.ProcessID,
    executionModeId: dto.ExecutionModeID,
    executionStatusId: dto.ExecutionStatusID,
    // Same offset-less UTC wire format as ProcessThreadExecution — see normalizeServerDate.
    startedAt: normalizeServerDate(dto.StartedAt),
    stoppedAt: normalizeServerDate(dto.StoppedAt),
    duration: dto.Duration ?? undefined,
    errorMessage: dto.ErrorMessage ?? undefined,
    totalNodes: dto.TotalNodes ?? undefined,
    completedNodes: dto.CompletedNodes ?? undefined,
    failedNodes: dto.FailedNodes ?? undefined,
    skippedNodes: dto.SkippedNodes ?? undefined,
  };
}

class ProcessExecutionApiClient extends BaseApiClient {
  /** Get a single ProcessExecution by its ProcessExecutionID — POST api/v1/process/process-executions/by-id. */
  public async getById(
    processExecutionId: number,
    options?: RequestOptions
  ): Promise<ApiResponse<ProcessExecutionCompletionInfo>> {
    // postQuery spreads ID/RaiseErrorIfNotFound at the request root, matching what
    // GetByIdWebRequest actually binds — post() nests under 'data' and silently 404s
    // (see the identical fix in processThreadExecutionApiClient.ts).
    const response = await this.postQuery<ProcessExecutionDto>(
      API_ENDPOINTS.PROCESS_EXECUTION.BY_ID,
      { ID: { ID: processExecutionId, ResID: '' }, RaiseErrorIfNotFound: false },
      options
    );
    if (!response.success || !response.data) {
      return response as unknown as ApiResponse<ProcessExecutionCompletionInfo>;
    }
    return { ...response, data: mapDto(response.data) };
  }
}

export const processExecutionApiClient = new ProcessExecutionApiClient();
